import React, { useState, useEffect, useCallback } from 'react';
import accountSsoService from '../services/accountSsoService';
import hotelService from '../services/hotelService';
import './SsoSettings.css';

const PROVIDERS = [
  { type: 'AZURE_AD', label: 'Azure AD',         defaultProtocol: 'OIDC', supportsOidc: true,  supportsSaml: true  },
  { type: 'GOOGLE',   label: 'Google Workspace', defaultProtocol: 'OIDC', supportsOidc: true,  supportsSaml: false },
  { type: 'AUTH0',    label: 'Auth0',             defaultProtocol: 'OIDC', supportsOidc: true,  supportsSaml: false },
  { type: 'PINGID',   label: 'PingID',            defaultProtocol: 'SAML', supportsOidc: true,  supportsSaml: true  },
];

const PROVIDER_ICONS = {
  AZURE_AD: (
    <svg width="36" height="36" viewBox="0 0 96 96" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="96" height="96" rx="12" fill="#f3f4f6"/>
      <path d="M51.5 14L27 55.5l8.5 1.5L51.5 30l16 26.5H28L20 70h56L51.5 14z" fill="#0078D4"/>
    </svg>
  ),
  GOOGLE: (
    <svg width="36" height="36" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
      <rect width="48" height="48" rx="8" fill="#f3f4f6"/>
      <path fill="#4285F4" d="M23.5 10c3.6 0 6.7 1.2 9.1 3.2l-3.8 3.8C27.4 15.6 25.6 15 23.5 15c-4.6 0-8.5 3.1-9.9 7.3l-4.4-3.4C11.8 13.5 17.2 10 23.5 10z"/>
      <path fill="#34A853" d="M9.2 24.5c0-1.2.2-2.3.5-3.4l4.4 3.4c-.1.6-.2 1.3-.2 2s.1 1.4.2 2l-4.4 3.4c-.3-1.1-.5-2.2-.5-3.4z"/>
      <path fill="#FBBC05" d="M23.5 33c2 0 3.8-.7 5.2-1.8l4.1 3.2C30.4 36.7 27.2 38 23.5 38c-6.3 0-11.7-3.5-14.3-8.7l4.4-3.4c1.4 4.2 5.3 7.1 9.9 7.1z"/>
      <path fill="#EA4335" d="M37.5 24.5c0-.8-.1-1.7-.2-2.5H23.5v5h7.8c-.4 2-1.5 3.6-3 4.7l-4.1-3.2c1.2-.9 2.1-2.3 2.4-3.9h-3.1v-5h14c.1.8.2 1.6.2 2.5 0 5.9-2.1 10.5-6 13.7l-4.1-3.2c2.5-2 4-5 4-8.1z"/>
    </svg>
  ),
  AUTH0: (
    <svg width="36" height="36" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
      <rect width="48" height="48" rx="8" fill="#f3f4f6"/>
      <path fill="#EB5424" d="M24 8l-8 5-3 9 11 3 11-3-3-9-8-5zm0 7c2.2 0 4 1.8 4 4s-1.8 4-4 4-4-1.8-4-4 1.8-4 4-4z"/>
      <path fill="#EB5424" d="M13 22l3 9 8 5 8-5 3-9-11 3-11-3z" opacity=".6"/>
    </svg>
  ),
  PINGID: (
    <svg width="36" height="36" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
      <rect width="48" height="48" rx="8" fill="#f3f4f6"/>
      <circle cx="24" cy="24" r="12" fill="#D5001C"/>
      <text x="24" y="29" textAnchor="middle" fill="#fff" fontSize="14" fontWeight="bold" fontFamily="Arial">P</text>
    </svg>
  ),
};

// Last step is always a form-fill CTA — referenced as { cta: true } in steps arrays
const INSTRUCTIONS = {
  AZURE_AD: {
    OIDC: {
      title: 'Azure AD — OIDC Setup',
      steps: [
        { text: 'In the **Azure Portal**, go to **Azure Active Directory → App registrations → New registration**.' },
        { text: 'Give the app a name (e.g. "HConnect SSO") and choose **Accounts in this organizational directory only**.' },
        { text: 'Under **Redirect URIs**, choose **Web** platform (not SPA) and add this URI:' },
        { callback: true },
        { text: 'Click **Register** to create the app.' },
        { text: 'Go to **Certificates & secrets → New client secret**. Copy the **Value** immediately — it won\'t be shown again.' },
        { text: 'From **Overview**, copy the **Application (client) ID** and the **Directory (tenant) ID**.' },
        { text: 'Enter the values in the form below and click **Save & Activate**.', cta: true },
      ],
      fieldHints: {
        client_id: 'Application (client) ID from Azure Overview',
        client_secret: 'Client secret Value (not the Secret ID)',
        issuer_url: 'https://login.microsoftonline.com/{Directory-tenant-ID}/v2.0',
      },
      note: 'Use Web platform (not SPA) when adding the redirect URI in Azure to avoid PKCE issues with Cognito.',
    },
    SAML: {
      title: 'Azure AD — SAML Setup',
      steps: [
        { text: 'In the **Azure Portal**, go to **Enterprise Applications → New application → Create your own application**.' },
        { text: 'Choose **Integrate any other application you don\'t find in the gallery**.' },
        { text: 'Go to **Single sign-on → SAML**.' },
        { text: 'Under **Basic SAML Configuration**, set the **Reply URL (Assertion Consumer Service URL)** to:' },
        { callback: true },
        { text: 'Go to **SAML Signing Certificate** and download the **Federation Metadata XML**, or copy the **App Federation Metadata URL**.' },
        { text: 'Paste the metadata URL or upload the XML file below, then click **Save & Activate**.', cta: true },
      ],
      note: 'Ensure the SAML attributes include email, given_name, and family_name claims.',
    },
  },
  GOOGLE: {
    OIDC: {
      title: 'Google Workspace — OIDC Setup',
      steps: [
        { text: 'Go to **Google Cloud Console → APIs & Services → Credentials → Create Credentials → OAuth 2.0 Client ID**.' },
        { text: 'Set the **Application type** to **Web application**.' },
        { text: 'Under **Authorised redirect URIs**, add:' },
        { callback: true },
        { text: 'Click **Create** and copy the **Client ID** and **Client Secret**.' },
        { text: 'Enter those values in the form below. Set **Issuer URL** to <code>https://accounts.google.com</code>.' },
        { text: 'Click **Save & Activate** to configure this provider.', cta: true },
      ],
      fieldHints: {
        client_id: 'OAuth 2.0 Client ID',
        client_secret: 'Client Secret',
        issuer_url: 'https://accounts.google.com',
      },
      note: 'Users must belong to your Google Workspace organisation domain.',
    },
  },
  AUTH0: {
    OIDC: {
      title: 'Auth0 — OIDC Setup',
      steps: [
        { text: 'In the **Auth0 Dashboard**, go to **Applications → Create Application → Regular Web Application**.' },
        { text: 'Under **Allowed Callback URLs**, add:' },
        { callback: true },
        { text: 'Go to the **Settings** tab and copy the **Domain**, **Client ID**, and **Client Secret**.' },
        { text: 'Enter those values in the form below. Set **Issuer URL** to <code>https://{your-auth0-domain}</code>.' },
        { text: 'Click **Save & Activate** to configure this provider.', cta: true },
      ],
      fieldHints: {
        client_id: 'Client ID from Auth0 application settings',
        client_secret: 'Client Secret from Auth0 application settings',
        issuer_url: 'https://{your-tenant}.auth0.com',
      },
      note: 'Ensure the application grants the openid, email, and profile scopes.',
    },
  },
  PINGID: {
    OIDC: {
      title: 'PingID — OIDC Setup',
      steps: [
        { text: 'In **PingOne**, go to **Connections → Applications → Add Application → OIDC Web App**.' },
        { text: 'Under **Redirect URIs**, add:' },
        { callback: true },
        { text: 'Copy the **Client ID**, **Client Secret**, and the **Issuer** URL from the application settings.' },
        { text: 'Enter those values in the form below and click **Save & Activate**.', cta: true },
      ],
      fieldHints: {
        client_id: 'Client ID from PingOne application',
        client_secret: 'Client Secret from PingOne application',
        issuer_url: 'https://auth.pingone.eu/{environment-id}/as',
      },
      note: 'The issuer URL is in the OIDC discovery document under your PingOne environment.',
    },
    SAML: {
      title: 'PingID — SAML Setup',
      steps: [
        { text: 'In **PingOne**, go to **Connections → Applications → Add Application → SAML Application**.' },
        { text: 'Set the **ACS URL** (Assertion Consumer Service URL) to:' },
        { callback: true },
        { text: 'Download the **SAML Metadata** XML from the application\'s configuration.' },
        { text: 'Upload the metadata XML file below and click **Save & Activate**.', cta: true },
      ],
      note: 'Map at minimum: email, given_name, and family_name in the SAML attribute mapping.',
    },
  },
};

const COGNITO_REDIRECT = `https://${process.env.REACT_APP_COGNITO_DOMAIN || '<cognito-domain>.auth.<region>.amazoncognito.com'}/oauth2/idpresponse`;

const emptyForm = {
  protocol: 'OIDC',
  client_id: '',
  client_secret: '',
  issuer_url: '',
  metadata_url: '',
  metadata_xml: '',
  authorize_scopes: 'openid email profile',
  attributes_request_method: 'GET',
  email_domains: '',
};

const SsoSettings = ({ accountId }) => {
  const [providers, setProviders]             = useState([]);
  const [selectedType, setSelectedType]       = useState(null);
  const [form, setForm]                       = useState(emptyForm);
  const [currentId, setCurrentId]             = useState(null);
  const [isCognitoConfigured, setIsCognito]   = useState(false);
  const [busy, setBusy]                       = useState(false);
  const [deleting, setDeleting]               = useState(false);
  const [message, setMessage]                 = useState(null);
  const [showInstructions, setShowInstructions] = useState(true);
  const [xmlFileName, setXmlFileName]         = useState('');

  // Role reference table state
  const [hotels, setHotels]                   = useState([]);
  const [selectedRoles, setSelectedRoles]     = useState(new Set());
  const [showRoleRef, setShowRoleRef]         = useState(true);
  const [copied, setCopied]                   = useState(false);

  useEffect(() => {
    if (accountId) {
      loadProviders();
      loadHotels();
    }
  }, [accountId]);

  const loadHotels = async () => {
    try {
      const data = await hotelService.getAllHotels({ accountid: accountId });
      setHotels(data.hotels || data || []);
    } catch {
      // ignore
    }
  };

  const loadProviders = async () => {
    try {
      const data = await accountSsoService.getProviders(accountId);
      setProviders(data.providers || []);
    } catch {
      // ignore
    }
  };

  const selectProvider = (type) => {
    setSelectedType(type);
    setMessage(null);
    const existing = providers.find(p => p.provider_type === type);
    if (existing) {
      const def = PROVIDERS.find(p => p.type === type);
      setForm({
        protocol: existing.protocol || def.defaultProtocol,
        client_id: existing.client_id || '',
        client_secret: '',
        issuer_url: existing.issuer_url || '',
        metadata_url: existing.metadata_url || '',
        metadata_xml: existing.metadata_xml || '',
        authorize_scopes: existing.authorize_scopes || 'openid email profile',
        attributes_request_method: existing.attributes_request_method || 'GET',
        email_domains: existing.email_domains || '',
      });
      setCurrentId(existing.id);
      setIsCognito(!!existing.cognito_configured);
    } else {
      const def = PROVIDERS.find(p => p.type === type);
      setForm({ ...emptyForm, protocol: def?.defaultProtocol || 'OIDC' });
      setCurrentId(null);
      setIsCognito(false);
    }
    setXmlFileName('');
  };

  /** Save config to DB then immediately push to Cognito. */
  const handleSaveAndActivate = async () => {
    setBusy(true);
    setMessage(null);
    try {
      // 1. Persist to DB
      const saved = await accountSsoService.saveProvider({
        account_id: accountId,
        provider_type: selectedType,
        ...form,
        client_secret: form.client_secret || undefined,
        metadata_xml: form.metadata_xml || undefined,
      });
      const savedId = saved.id || currentId;
      setCurrentId(savedId);

      // 2. Configure Cognito
      const result = await accountSsoService.configureProvider(savedId);
      setIsCognito(true);
      await loadProviders();
      setMessage({ type: 'success', text: result.message || 'SSO provider activated successfully.' });
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Operation failed.';
      setMessage({ type: 'error', text: msg });
    } finally {
      setBusy(false);
    }
  };

  /** Save to DB only (no Cognito push). */
  const handleSaveOnly = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const saved = await accountSsoService.saveProvider({
        account_id: accountId,
        provider_type: selectedType,
        ...form,
        client_secret: form.client_secret || undefined,
        metadata_xml: form.metadata_xml || undefined,
      });
      setCurrentId(saved.id || currentId);
      setIsCognito(false);
      await loadProviders();
      setMessage({ type: 'success', text: 'Configuration saved. Click "Save & Activate" when ready to apply to Cognito.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Failed to save.' });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!currentId || !window.confirm('Delete this SSO configuration? This will also remove the provider from Cognito.')) return;
    setDeleting(true);
    try {
      await accountSsoService.deleteProvider(currentId);
      setSelectedType(null);
      setCurrentId(null);
      setForm(emptyForm);
      setIsCognito(false);
      setMessage(null);
      await loadProviders();
    } catch {
      setMessage({ type: 'error', text: 'Failed to delete configuration.' });
    } finally {
      setDeleting(false);
    }
  };

  const handleXmlUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setXmlFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => setForm(f => ({ ...f, metadata_xml: ev.target.result, metadata_url: '' }));
    reader.readAsText(file);
  };

  const selectedDef  = PROVIDERS.find(p => p.type === selectedType);
  const instructions = selectedType ? INSTRUCTIONS[selectedType]?.[form.protocol] : null;

  const renderStepText = (text) =>
    text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/<code>(.*?)<\/code>/g, '<code>$1</code>');

  // Build the flat list of all possible role rows
  const roleRows = [
    { key: '_ADMIN', label: 'Whole Chain', scope: 'Chain', role: 'Admin', roleStr: '_ADMIN' },
    { key: '_USER',  label: 'Whole Chain', scope: 'Chain', role: 'User',  roleStr: '_USER'  },
    ...hotels.flatMap(h => [
      { key: `${h.hotelid}_ADMIN`, label: h.hotelname, scope: `Hotel ID: ${h.hotelid}`, role: 'Admin', roleStr: `${h.hotelid}_ADMIN` },
      { key: `${h.hotelid}_USER`,  label: h.hotelname, scope: `Hotel ID: ${h.hotelid}`, role: 'User',  roleStr: `${h.hotelid}_USER`  },
    ]),
  ];

  const generatedRoleString = roleRows
    .filter(r => selectedRoles.has(r.key))
    .map(r => r.roleStr)
    .join(',');

  const toggleRoleRow = (key) => {
    setSelectedRoles(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
    setCopied(false);
  };

  const handleCopyRoles = () => {
    if (!generatedRoleString) return;
    navigator.clipboard.writeText(generatedRoleString).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="sso-settings">
      <h2>Single Sign-On (SSO)</h2>
      <p className="section-subtitle">
        Connect an identity provider so your users can sign in with their existing corporate credentials.
        Select a provider below, follow the setup steps, fill in the credentials, and click <strong>Save &amp; Activate</strong>.
      </p>

      {/* Provider picker */}
      <div className="sso-provider-grid">
        {PROVIDERS.map(p => {
          const existing = providers.find(pr => pr.provider_type === p.type);
          return (
            <button
              key={p.type}
              className={`sso-provider-card ${selectedType === p.type ? 'active' : ''}`}
              onClick={() => selectProvider(p.type)}
            >
              {existing && (
                <span className={`provider-status ${existing.cognito_configured ? '' : 'unconfigured'}`} />
              )}
              <span className="provider-icon">{PROVIDER_ICONS[p.type]}</span>
              <span className="provider-label">{p.label}</span>
            </button>
          );
        })}
      </div>

      {/* Role reference table — shown always, independent of provider selection */}
      <div className="sso-role-ref">
        <div className="sso-instructions-header" onClick={() => setShowRoleRef(s => !s)}>
          <span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 6, verticalAlign: 'middle' }}>
              <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            Role Reference — Permission Strings for your IDP
          </span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            style={{ transform: showRoleRef ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }}>
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </div>
        {showRoleRef && (
          <div className="sso-role-ref-body">
            <p className="role-ref-intro">
              Permissions are managed within the application. When an SSO user logs in for the first time
              they are automatically provisioned with no access. A superadmin can then grant them access
              to the appropriate chains and hotels from the Users section.
            </p>
            <table className="role-ref-table">
              <thead>
                <tr>
                  <th style={{ width: 36 }}></th>
                  <th>Scope</th>
                  <th>Role</th>
                  <th>Permission String</th>
                </tr>
              </thead>
              <tbody>
                {roleRows.map(row => (
                  <tr key={row.key} className={selectedRoles.has(row.key) ? 'selected' : ''} onClick={() => toggleRoleRow(row.key)}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedRoles.has(row.key)}
                        onChange={() => toggleRoleRow(row.key)}
                        onClick={e => e.stopPropagation()}
                      />
                    </td>
                    <td>
                      <span className="role-scope-label">{row.label}</span>
                      <span className="role-scope-id">{row.scope}</span>
                    </td>
                    <td>
                      <span className={`role-badge ${row.role === 'Admin' ? 'admin' : 'user'}`}>{row.role}</span>
                    </td>
                    <td>
                      <code className="role-str">{row.roleStr}</code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="role-ref-output">
              <input
                type="text"
                readOnly
                className="role-str-input"
                value={generatedRoleString}
                placeholder="Select rows above to generate the permission string"
              />
              <button
                className={`btn-copy-roles ${copied ? 'copied' : ''}`}
                onClick={handleCopyRoles}
                disabled={!generatedRoleString}
              >
                {copied ? (
                  <>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 4 }}>
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    Copied
                  </>
                ) : (
                  <>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 4 }}>
                      <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                    </svg>
                    Copy
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedType && (
        <>
          {message && (
            <div className={`sso-message ${message.type}`}>{message.text}</div>
          )}

          {/* Setup instructions — shown first so admin reads before filling the form */}
          {instructions && (
            <div className="sso-instructions">
              <div className="sso-instructions-header" onClick={() => setShowInstructions(s => !s)}>
                <span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 6, verticalAlign: 'middle' }}>
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  Setup Instructions — {instructions.title}
                </span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                  style={{ transform: showInstructions ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }}>
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </div>
              {showInstructions && (
                <div className="sso-instructions-body">
                  <ol>
                    {instructions.steps.map((step, i) => {
                      if (step.callback) {
                        return (
                          <li key={i}>
                            Add this Cognito redirect URI as an allowed callback / redirect URL in your identity provider:
                            <div className="callback-url-box">{COGNITO_REDIRECT}</div>
                          </li>
                        );
                      }
                      if (step.cta) {
                        return (
                          <li key={i} className="step-cta">
                            <span dangerouslySetInnerHTML={{ __html: renderStepText(step.text) }} />
                            {' '}Fill in the fields in the form below, then click the <strong>Save &amp; Activate</strong> button.
                          </li>
                        );
                      }
                      return (
                        <li key={i} dangerouslySetInnerHTML={{ __html: renderStepText(step.text) }} />
                      );
                    })}
                  </ol>
                  {instructions.note && (
                    <div className="note">{instructions.note}</div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Credentials form */}
          <div className="sso-config-panel">
            <div className="config-header">
              <span className="config-title">{selectedDef?.label} Credentials</span>
              <span className={`config-status-badge ${isCognitoConfigured ? 'configured' : 'unconfigured'}`}>
                {isCognitoConfigured ? '● Active in Cognito' : '○ Not yet activated'}
              </span>
            </div>

            {/* Protocol toggle */}
            {selectedDef?.supportsOidc && selectedDef?.supportsSaml && (
              <div className="protocol-toggle">
                <button className={form.protocol === 'OIDC' ? 'active' : ''} onClick={() => setForm(f => ({ ...f, protocol: 'OIDC' }))}>OIDC</button>
                <button className={form.protocol === 'SAML' ? 'active' : ''} onClick={() => setForm(f => ({ ...f, protocol: 'SAML' }))}>SAML</button>
              </div>
            )}

            <div className="sso-form-grid">
              {form.protocol === 'OIDC' ? (
                <>
                  <div className="sso-form-group">
                    <label>Client ID <span className="required">*</span></label>
                    <input
                      type="text"
                      value={form.client_id}
                      onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))}
                      placeholder={instructions?.fieldHints?.client_id || 'Client ID'}
                    />
                  </div>
                  <div className="sso-form-group">
                    <label>Client Secret <span className="required">*</span></label>
                    <input
                      type="password"
                      value={form.client_secret}
                      onChange={e => setForm(f => ({ ...f, client_secret: e.target.value }))}
                      placeholder={currentId ? 'Leave blank to keep existing secret' : (instructions?.fieldHints?.client_secret || 'Client secret')}
                    />
                    {currentId && <span className="field-hint">Leave blank to keep the existing secret.</span>}
                  </div>
                  <div className="sso-form-group full-width">
                    <label>Issuer URL <span className="required">*</span></label>
                    <input
                      type="url"
                      value={form.issuer_url}
                      onChange={e => setForm(f => ({ ...f, issuer_url: e.target.value }))}
                      placeholder={instructions?.fieldHints?.issuer_url || 'https://...'}
                    />
                    <span className="field-hint">The OIDC issuer / discovery endpoint (without /.well-known/openid-configuration).</span>
                  </div>
                  <div className="sso-form-group">
                    <label>Scopes</label>
                    <input type="text" value={form.authorize_scopes} onChange={e => setForm(f => ({ ...f, authorize_scopes: e.target.value }))} />
                  </div>
                  <div className="sso-form-group">
                    <label>Attributes Request Method</label>
                    <select value={form.attributes_request_method} onChange={e => setForm(f => ({ ...f, attributes_request_method: e.target.value }))}>
                      <option value="GET">GET</option>
                      <option value="POST">POST</option>
                    </select>
                  </div>
                </>
              ) : (
                <>
                  <div className="sso-form-group full-width">
                    <label>Metadata URL</label>
                    <input
                      type="url"
                      value={form.metadata_url}
                      onChange={e => setForm(f => ({ ...f, metadata_url: e.target.value, metadata_xml: '' }))}
                      placeholder="https://..."
                    />
                    <span className="field-hint">Provide a metadata URL or upload the XML file below — either is accepted.</span>
                  </div>
                  <div className="sso-form-group full-width">
                    <label>Metadata XML File</label>
                    <div className="sso-file-upload">
                      <label className="btn-upload-xml">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                        </svg>
                        Upload XML
                        <input type="file" accept=".xml,text/xml" onChange={handleXmlUpload} style={{ display: 'none' }} />
                      </label>
                      <span className="file-name">{xmlFileName || (form.metadata_xml ? 'File loaded' : 'No file selected')}</span>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Email domain routing */}
            <div className="sso-form-group full-width sso-email-domains">
              <label>Email Domains</label>
              <input
                type="text"
                value={form.email_domains}
                onChange={e => setForm(f => ({ ...f, email_domains: e.target.value }))}
                placeholder="acme.com, acme.co.uk"
              />
              <span className="field-hint">
                Comma-separated domains (e.g. acme.com, acme.co.uk). Users entering an email with these domains on the login screen will be automatically redirected to this provider.
              </span>
            </div>

            {/* Primary action */}
            <div className="sso-config-actions">
              <button className="btn-sso-activate" onClick={handleSaveAndActivate} disabled={busy}>
                {busy ? (
                  <>
                    <span className="btn-spinner" /> Activating…
                  </>
                ) : (
                  <>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 6 }}>
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    {isCognitoConfigured ? 'Update & Re-activate' : 'Save & Activate'}
                  </>
                )}
              </button>

              <button className="btn-sso-save-only" onClick={handleSaveOnly} disabled={busy}>
                Save only
              </button>

              {currentId && (
                <button className="btn-sso-delete" onClick={handleDelete} disabled={deleting || busy}>
                  {deleting ? 'Deleting…' : 'Delete'}
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SsoSettings;
